import React, { createContext, useCallback, useContext, useEffect, useRef } from 'react';
import { io, Socket } from 'socket.io-client';
import { useSelector } from 'react-redux';
import { RootState } from '../../core/store/store';
import { getApiBaseUrl, getSocketIoPath } from '../../core/config/api';

/**
 * Bus temps réel central.
 *
 * Une SEULE connexion Socket.IO pour toute l'appli. Le serveur émet un signal léger
 * `data:invalidate { domain, meta }` après chaque mutation (voir
 * server/services/RealtimeInvalidation.ts). Chaque écran s'abonne au(x) domaine(s) qui
 * l'intéresse(nt) via `useRealtimeInvalidation(domain, refetch)` et rafraîchit ses données.
 *
 * Remplace à terme les sockets ad hoc par écran ; cohabite avec eux pour l'instant.
 */

type InvalidateMeta = Record<string, unknown> | null;
type InvalidateHandler = (meta: InvalidateMeta) => void;

type RealtimeContextValue = {
  subscribe: (domain: string, handler: InvalidateHandler) => () => void;
};

const RealtimeContext = createContext<RealtimeContextValue | null>(null);

export const RealtimeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const currentUser = useSelector((s: RootState) => s.user.currentUser);
  const userId = currentUser?.id;

  const handlersRef = useRef<Map<string, Set<InvalidateHandler>>>(new Map());
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    if (!userId) return;

    const token = localStorage.getItem('token') || '';
    const socket = io(getApiBaseUrl(), {
      path: getSocketIoPath(),
      auth: { token },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
    });
    socketRef.current = socket;

    socket.on('connect', () => {
      socket.emit('join:user', userId);
    });

    socket.on('data:invalidate', (payload: { domain?: string; meta?: InvalidateMeta }) => {
      const domain = payload?.domain;
      if (!domain) return;
      const set = handlersRef.current.get(domain);
      if (!set) return;
      set.forEach((handler) => {
        try {
          handler(payload.meta ?? null);
        } catch {
          /* un abonné ne doit pas casser les autres */
        }
      });
    });

    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [userId]);

  const subscribe = useCallback((domain: string, handler: InvalidateHandler) => {
    let set = handlersRef.current.get(domain);
    if (!set) {
      set = new Set<InvalidateHandler>();
      handlersRef.current.set(domain, set);
    }
    set.add(handler);
    return () => {
      const current = handlersRef.current.get(domain);
      if (!current) return;
      current.delete(handler);
      if (current.size === 0) handlersRef.current.delete(domain);
    };
  }, []);

  return <RealtimeContext.Provider value={{ subscribe }}>{children}</RealtimeContext.Provider>;
};

/**
 * S'abonne aux invalidations d'un domaine. `handler` est appelé (avec les métadonnées
 * éventuelles) chaque fois qu'une donnée du domaine change côté serveur. Le handler peut
 * être une fonction inline : il est capturé par ref, sans re-souscription à chaque rendu.
 */
export function useRealtimeInvalidation(domain: string | null, handler: InvalidateHandler): void {
  const ctx = useContext(RealtimeContext);
  const savedHandler = useRef(handler);
  savedHandler.current = handler;

  useEffect(() => {
    if (!ctx || !domain) return;
    return ctx.subscribe(domain, (meta) => savedHandler.current(meta));
  }, [ctx, domain]);
}

export default RealtimeProvider;
